/**
 * Service API de base
 * Gère les appels HTTP avec Axios et la configuration centralisée
 */
import axios from 'axios';
import store from '@/store';
import { BASE_URL } from '@/components/globalConfig/globalConstConfig';
import cacheService from './cache.service';

class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Intercepteur pour ajouter le token automatiquement depuis Vuex
    this.client.interceptors.request.use(
      (config) => {
        // Récupérer le token depuis Vuex store (plus sécurisé que localStorage)
        const token = store.state.userAuthentified?.token;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          console.warn('⚠️ Aucun token trouvé dans Vuex store pour la requête:', config.url);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Variables pour gérer le refresh token
    this.isRefreshing = false;
    this.failedQueue = [];

    // Intercepteur pour gérer les erreurs globalement + refresh token
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // Gestion centralisée des erreurs
        if (error.response) {
          const status = error.response.status;
          const url = error.config?.url || 'URL inconnue';
          
          switch (status) {
            case 401:
              // Token expiré ou invalide - Tenter le refresh automatique
              console.warn('🔒 [401] Token expiré pour:', url);
              
              // Ne pas tenter de refresh sur l'endpoint de refresh lui-même
              if (url.includes('refreshToken') || originalRequest._retry) {
                console.error('❌ Impossible de rafraîchir le token, déconnexion nécessaire');
                await store.dispatch('destroySession');
                // Utiliser router.push au lieu de window.location pour éviter le rechargement
                const router = require('@/router').default;
                router.push('/').catch(() => {});
                break;
              }

              // Gestion du refresh token avec file d'attente
              if (this.isRefreshing) {
                return new Promise((resolve, reject) => {
                  this.failedQueue.push({ resolve, reject });
                })
                  .then((token) => {
                    originalRequest.headers['Authorization'] = `Bearer ${token}`;
                    return this.client(originalRequest);
                  })
                  .catch((err) => Promise.reject(err));
              }

              originalRequest._retry = true;
              this.isRefreshing = true;

              try {
                const newToken = await this.refreshToken();
                
                // Mettre à jour le header de la requête originale
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                
                // Traiter la file d'attente
                this.processQueue(null, newToken);
                
                // Relancer la requête originale
                return this.client(originalRequest);
              } catch (refreshError) {
                this.processQueue(refreshError, null);
                console.error('❌ Échec du refresh token, déconnexion');
                await store.dispatch('destroySession');
                // Utiliser router.push au lieu de window.location pour éviter le rechargement
                const router = require('@/router').default;
                router.push('/').catch(() => {});
                return Promise.reject(refreshError);
              } finally {
                this.isRefreshing = false;
              }
              
            case 403:
              // Accès interdit
              console.warn('⛔ [403] Accès interdit pour:', url);
              break;
            case 404:
              console.warn('🔍 [404] Ressource non trouvée:', url);
              break;
            case 500:
              console.error('💥 [500] Erreur serveur pour:', url);
              break;
            default:
              console.error(`❌ [${status}] Erreur API pour:`, url);
          }
        } else if (error.request) {
          console.error('📡 Pas de réponse du serveur');
        } else {
          console.error('⚙️ Erreur de configuration:', error.message);
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Effectue une requête GET avec support du cache
   * @param {string} endpoint - L'endpoint de l'API
   * @param {object} config - Configuration Axios (peut inclure { cache: false } pour bypass)
   */
  async get(endpoint, config = {}) {
    // Vérifier si le cache doit être bypassé
    const useCache = config.cache !== false;
    const axiosConfig = { ...config };
    delete axiosConfig.cache; // Supprimer la propriété cache avant d'envoyer à Axios

    if (!useCache) {
      const response = await this.client.get(endpoint, axiosConfig);
      return response.data;
    }

    // Utiliser le cache
    return await cacheService.execute(
      endpoint,
      async () => {
        const response = await this.client.get(endpoint, axiosConfig);
        return response.data;
      },
      {},
      axiosConfig
    );
  }

  /**
   * Effectue une requête POST
   */
  async post(endpoint, data = {}, config = {}) {
    const response = await this.client.post(endpoint, data, config);
    return response.data;
  }

  /**
   * Effectue une requête PUT
   */
  async put(endpoint, data = {}, config = {}) {
    const response = await this.client.put(endpoint, data, config);
    return response.data;
  }

  /**
   * Effectue une requête DELETE
   */
  async delete(endpoint, config = {}) {
    const response = await this.client.delete(endpoint, config);
    return response.data;
  }

  /**
   * POST utilisé pour récupérer des données (pattern de l'API backend)
   * Certains endpoints utilisent POST au lieu de GET pour la récupération
   * Support du cache comme pour GET
   * @param {string} endpoint - L'endpoint de l'API
   * @param {object} data - Données à envoyer
   * @param {object} config - Configuration (peut inclure { cache: false } pour bypass)
   */
  async postForGet(endpoint, data = {}, config = {}) {
    // Vérifier si le cache doit être bypassé
    const useCache = config.cache !== false;
    const axiosConfig = { ...config };
    delete axiosConfig.cache;

    if (!useCache) {
      return await this.post(endpoint, data, axiosConfig);
    }

    // Utiliser le cache (les données POST font partie de la clé de cache)
    return await cacheService.execute(
      endpoint,
      async () => {
        return await this.post(endpoint, data, axiosConfig);
      },
      data,
      axiosConfig
    );
  }

  /**
   * Traite la file d'attente des requêtes en attente de refresh token
   */
  processQueue(error, token = null) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  }

  /**
   * Rafraîchit le token d'authentification
   */
  async refreshToken() {
    const refreshToken = store.state.userAuthentified?.refreshToken;
    const currentToken = store.state.userAuthentified?.token;

    if (!refreshToken) {
      throw new Error('Aucun refresh token disponible');
    }

    const dataToRefreshToSend = {
      data: {
        refreshToken: refreshToken,
        token: currentToken,
      },
    };

    try {
      const response = await this.client.post('refreshToken', dataToRefreshToSend);
      
      if (response.data?.status?.code === 800 && response.data?.item?.token) {
        // Mettre à jour le token dans Vuex
        store.commit('updateToken', response.data.item);
        return response.data.item.token;
      } else {
        throw new Error('Échec du rafraîchissement du token');
      }
    } catch (error) {
      console.error('❌ Erreur lors du refresh token:', error);
      throw error;
    }
  }

  /**
   * Obtient les headers avec le token depuis Vuex
   */
  getHeaders(token = null) {
    const authToken = token || store.state.userAuthentified?.token;
    return {
      Authorization: authToken ? `Bearer ${authToken}` : '',
    };
  }

  /**
   * Invalide le cache pour un pattern spécifique
   * Utile après une création/modification/suppression
   */
  invalidateCache(pattern) {
    return cacheService.invalidatePattern(pattern);
  }

  /**
   * Vide complètement le cache
   */
  clearCache() {
    return cacheService.clear();
  }

  /**
   * Obtient les statistiques du cache
   */
  getCacheStats() {
    return cacheService.getStats();
  }
}

export default new ApiService();
